import {
  Attendance,
  AttendanceCompletion,
  AttendanceStatus,
  Event,
  Shift,
  Signup,
  Source,
} from '@amzn/red-velvet-api';
import { asDateString, asTimeString } from './dateFormatting';
import { LoginData } from '../hooks/login/login';

export function generateShiftStartDateInfo(
  shift: Shift,
  selectedTimezone: string,
  language: string,
  defaultDisplayValue: string
) {
  const startDate = shift.startDateTime ? new Date(shift.startDateTime) : null;
  const endDate = shift.endDateTime ? new Date(shift.endDateTime) : null;
  if (startDate && endDate) {
    return asDateString(startDate, selectedTimezone, language);
  } else {
    return defaultDisplayValue;
  }
}

export function generateShiftStartTimeInfo(
  shift: Shift,
  selectedTimezone: string,
  language: string,
  defaultDisplayValue: string
) {
  const startDateTime = shift.startDateTime ? new Date(shift.startDateTime) : null;
  const endDateTime = shift.endDateTime ? new Date(shift.endDateTime) : null;
  if (startDateTime && endDateTime) {
    return asTimeString(startDateTime, selectedTimezone, language, {
      removeTimeZone: true,
    });
  } else {
    return defaultDisplayValue;
  }
}

export function generateShiftNumberOfSignedUpInfo(shift: Shift, signups: Signup[] | undefined) {
  const numberOfGuests = shift.totalNumberOfGuests ?? 0;
  const signupCount = signups?.length ?? 0;
  return { signupCount, numberOfGuests };
}

export function generateShiftNumberOfPeopleAttended(attendances: Attendance[] | undefined) {
  return attendances
    ? `${attendances.filter((attendance) => attendance.attendanceStatus === AttendanceStatus.ATTENDED).length}`
    : '0';
}

type actionLinkType = 'signup';

export function generateShiftActionLink(
  attendanceCompletion: string | undefined,
  shiftId: string,
  eventId: string,
  urlFeatures: string | null,
  action: actionLinkType = 'signup'
) {
  const eventDetailUrl = `/view-event?id=${eventId}&coordinator-action=${action}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const coordinatorTrackAttendanceUrl = `/track-attendance?page=1&eventId=${eventId}&shiftId=${shiftId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  if (!attendanceCompletion || AttendanceCompletion.UPCOMING === attendanceCompletion) {
    return eventDetailUrl;
  } else {
    return coordinatorTrackAttendanceUrl;
  }
}
export function generateShiftTotals(shiftLists?: Shift[]) {
  let totalSpots = 0;
  let totalShifts = 0;
  (shiftLists ?? []).forEach((shift) => {
    if (!shift.maxSignup) {
      totalSpots = Infinity;
      totalShifts++;
    } else if (shift.maxSignup && shift.signupsRemaining > 0) {
      totalSpots = totalSpots === Infinity ? Infinity : totalSpots + shift.signupsRemaining;
      totalShifts++;
    }
  });
  return {
    totalSpots,
    totalShifts,
  };
}

export function isEventOwner(event: Event, users: LoginData) {
  return event.ownerId === users.employeeId || event.ownerEmail?.includes(users.userId);
}

export function isEventAmbassador(event: Event, users: LoginData) {
  return event.ambassadors?.includes(users.employeeId);
}

export function canManageGivehubEvent(event: Event, users: LoginData): boolean {
  return Boolean(
    event.eventId &&
      event.source === Source.GIVEHUB &&
      (isEventOwner(event, users) ||
        isEventAmbassador(event, users) ||
        event.additionalCoordinators?.includes(users.userId))
  );
}

export function canManageVCEvent(event: Event, users: LoginData) {
  return (
    event.eventId &&
    event.source === Source.VC &&
    (isEventOwner(event, users) ||
      isEventAmbassador(event, users) ||
      event.additionalCoordinators?.includes(users.userId))
  );
}
